import { IComponentOptions, IPromise, IQService, IRootScopeService, IScope } from "angular";
import { StateService, UIRouterGlobals } from "@uirouter/angularjs";
import { Observable, Subscription, take, takeUntil } from "rxjs";
import { GtmhubController } from "@gtmhub/core";
import { DocumentExporterService } from "@gtmhub/document-exporter/document-exporter-service";
import { UIErrorHandlingService } from "@gtmhub/error-handling/services/error-handling-service";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { filtersOverrideForOkrName, filtersOverrideForTagTitle } from "@gtmhub/goals/filter-utils";
import { localize } from "@gtmhub/localization";
import { BASE_OKR_VIEWS_STATE, BASE_OKR_VIEW_STATE } from "@gtmhub/okrs/okrs.states";
import { toRestLayerRequest } from "@gtmhub/shared/components/grid-filtering-bar/rest-layer-request-builder";
import { SimplePermissionsAccessType } from "@gtmhub/shared/components/simple-permissions/simple-permissions.component";
import { determineAccessType } from "@gtmhub/shared/components/simple-permissions/simple-permissions.utils";
import { triggerFileDownload } from "@gtmhub/shared/dom";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { PermissionsService } from "@gtmhub/users";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { OkrViewEventsManager } from "@webapp/okrs/okr-views/utils/okr-view.events";
import { IAccess, IAllowedActionsMap, IUserItemAllowedActions } from "@webapp/sessions/models/sessions.model";
import { DropdownMenuItem } from "@webapp/shared/dropdown/dropdown.models";
import { ShareableFilter, ShareableFilterPostDTO } from "@webapp/shared/unified-filters/models/shareable-filters.models";
import { ShareableFiltersModalsService } from "@webapp/shared/unified-filters/services/shareable-filters-modals.service";
import { ShareableFiltersFacade } from "@webapp/shared/unified-filters/services/shareable-filters/shareable-filters-facade.service";
import { UiToastService } from "@webapp/ui/toast/services/toast.service";
import { OkrViewEvents } from "../../events";
import { navigateToSingleOkrView } from "../../utils/utils";

interface OkrViewStateParams {
  okrViewId: string;
}

export class OkrViewCtrl extends GtmhubController {
  public topNavBarSubtitle: string;
  public okrView: ShareableFilter;
  public isLoading: boolean;
  public isMarkedAsFavorite: boolean;
  public isCurrentUserViewOnly: boolean;
  public currentUserAllowedActionsMap: IAllowedActionsMap;
  public dropdownMenuItems: DropdownMenuItem[];
  public isError: boolean;
  public showSimplePermissionsDropdown = false;
  public url: string;
  public shouldDisplayShareableLinkForm: boolean;
  public currentAccess: IAccess;

  public static $inject = [
    "$scope",
    "ShareableFiltersFacade",
    "ShareableFiltersModalsService",
    "UiToastService",
    "FavoritesManagerFactory",
    "$state",
    "$stateParams",
    "$q",
    "$uiRouterGlobals",
    "$rootScope",
    "UIErrorHandlingService",
    "PermissionsService",
    "Ng1ConfirmDeleteService",
    "DocumentExporterService",
  ];

  private favoritesManager: FavoritesManager;
  private favoritesSubscription: Subscription;
  private okrViewEvents: OkrViewEvents;
  private okrViewEventsManager: OkrViewEventsManager;
  private destroy$: Observable<unknown>;

  constructor(
    private $scope: IScope,
    private shareableFiltersFacade: ShareableFiltersFacade,
    private shareableFiltersModalsService: ShareableFiltersModalsService,
    private toastService: UiToastService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private $state: StateService,
    private $stateParams: OkrViewStateParams,
    private $q: IQService,
    private $uiRouterGlobals: UIRouterGlobals,
    private $rootScope: IRootScopeService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private permissionsService: PermissionsService,
    private confirmDeleteService: Ng1ConfirmDeleteService,
    private documentExporterService: DocumentExporterService
  ) {
    super();

    this.destroy$ = new Ng1DestroyService(this.$scope);
    this.okrViewEvents = new OkrViewEvents(this.$rootScope);
    this.initOkrViewEventSubscriptions();
  }

  public $onInit(): void {
    this.topNavBarSubtitle = localize("okr_views");

    if (this.checkIfShouldRedirectToDefaultSubState()) {
      this.redirectToDefaultSubStateIfNeeded();
    }

    this.initFavourites();
    this.proceedLoadingOkrViewData();

    this.$q.all([this.getOkrView(), this.getCurrentUserAllowedActions()]).then(
      ([okrView, actions]) => {
        this.okrView = okrView;
        this.currentUserAllowedActionsMap = actions.currentUserActions.reduce((acc, action) => ((acc[action] = true), acc), {});
        this.setShareableLinkFormVisibility(this.okrView.access);

        const currentState = this.$uiRouterGlobals.current.name;
        this.url = this.$state.href(currentState, { okrViewId: this.$stateParams.okrViewId }, { absolute: true });

        this.checkForNewlyCreatedView(okrView.id);
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
        this.isError = true;
      }
    );
  }

  public $onDestroy(): void {
    this.favoritesSubscription.unsubscribe();
  }

  public get hasModifyPermissions() {
    return this.currentUserAllowedActionsMap.modifyPermissions;
  }

  public get hasAccessChanged() {
    return this.currentAccess ? !angular.equals(this.okrView.access.permissions, this.currentAccess.permissions) : false;
  }

  public copyShareableLinkWithAppliedPermanentFilters(): void {
    const currentState = this.$uiRouterGlobals.current.name;
    this.shareableFiltersFacade.copyShareableOkrViewLink(currentState, { okrViewIs: this.$stateParams.okrViewId });
  }

  public addToFavorites(): void {
    this.favoritesManager.addToFavorites({ id: this.$stateParams.okrViewId }, "filter", this.getNavItem());
    this.setMarkedAsFavorite();
  }

  public removeFromFavorites(): void {
    this.favoritesManager.removeFromFavorites({ id: this.$stateParams.okrViewId }, this.getNavItem());
    this.setMarkedAsFavorite();
  }

  public simplePermissionsChangeHandler(args: { access: IAccess }): void {
    if (!this.hasModifyPermissions) return;

    this.currentAccess = args.access;
    this.setShareableLinkFormVisibility(args.access);
  }

  public simplePermissionsToggleHandler(isVisible: boolean): void {
    this.showSimplePermissionsDropdown = isVisible;

    if (isVisible || !this.hasModifyPermissions || !this.hasAccessChanged || !this.currentAccess) return;

    this.shareableFiltersFacade
      .patchShareableFilter$(this.$stateParams.okrViewId, { access: this.currentAccess })
      .pipe(take(1))
      .subscribe({
        next: (updateOkrView) => {
          this.okrView.access = updateOkrView.access;
          this.okrViewEventsManager.notifyOkrViewAccessChanged(updateOkrView.access);
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  }

  public setShareableLinkFormVisibility = (access: IAccess): void => {
    const mappedAccess = determineAccessType(access);
    const shouldHideShareableLinkForm =
      mappedAccess === SimplePermissionsAccessType.private || (mappedAccess === SimplePermissionsAccessType.restricted && access.permissions.length <= 1);

    this.shouldDisplayShareableLinkForm = !shouldHideShareableLinkForm;
    this.$scope.$evalAsync();
  };

  public cloneOkrView(): void {
    const clonedFilterPayload: ShareableFilterPostDTO = {
      name: `${this.okrView.name} Clone`,
      filters: this.okrView.filters,
      sorting: this.okrView.sorting,
      columns: this.okrView.columns,
    };

    this.shareableFiltersFacade
      .postShareableFilter$(clonedFilterPayload)
      .pipe(take(1))
      .subscribe({
        next: (shareableFilter) => {
          navigateToSingleOkrView(this.$state, shareableFilter.id);
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  }

  public confirmDeleteOkrView(): void {
    this.confirmDeleteService.confirmDelete({ title: localize("delete_okr_view"), description: localize("this_cant_be_undone") }).subscribe(() => {
      this.shareableFiltersFacade
        .deleteShareableFilter$(this.okrView.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.okrViewEvents.broadcastOkrViewDeleted(this.okrView.id);
            this.$state.go("gtmhub.okrViews");
          },
          error: (error) => {
            this.uiErrorHandlingService.handleModal(error);
          },
        });
    });
  }

  public updateAccess(access: IAccess): void {
    if (!this.currentUserAllowedActionsMap.modifyPermissions) return;

    this.shareableFiltersFacade
      .patchShareableFilter$(this.$stateParams.okrViewId, { access })
      .pipe(take(1))
      .subscribe({
        next: (updateOkrView) => {
          this.okrView.access = updateOkrView.access;
          this.okrViewEventsManager.notifyOkrViewAccessChanged(updateOkrView.access);
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  }

  public exportToExcel(): void {
    const generateOkrViewFileName = (nameFromBackend, okrViewName) => {
      return nameFromBackend
        .split(".")
        .map((el: string, i: number) => (i === 1 ? okrViewName : el))
        .join(".");
    };
    const filtersOverride = { ...filtersOverrideForTagTitle(), ...filtersOverrideForOkrName() };
    const { filter, sort } = toRestLayerRequest({
      filters: [this.okrView.filters],
      sorting: this.okrView.sorting,
      filtersOverride: filtersOverride,
    });
    const hostSessionId = this.okrView.id;

    this.documentExporterService.exportGoalsGridToExcel({ filter, sort, hostSessionId }).then(
      (response) => {
        const generatedExcel = new Blob([response.data], { type: "application/octet-stream" });
        const fileName = generateOkrViewFileName(response.headers("File-Name"), this.okrView.name);
        triggerFileDownload(generatedExcel, fileName);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  }

  private initOkrViewEventSubscriptions(): void {
    this.okrViewEventsManager = new OkrViewEventsManager(this.$stateParams.okrViewId);

    this.okrViewEventsManager
      .okrViewPrivateAccessChangeRequested$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.showSimplePermissionsDropdown = true;
        this.$scope.$evalAsync();
      });

    this.okrViewEventsManager
      .okrViewFilterChanged$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ filters, sorting }) => {
        this.okrView.filters = filters;
        this.okrView.sorting = sorting;
        this.$scope.$evalAsync();
      });
  }

  private initFavourites(): void {
    this.favoritesManager = this.favoritesManagerFactory(["session", "filter"]);
    this.favoritesSubscription = this.favoritesManager.initFavorites().subscribe(() => this.setMarkedAsFavorite());
  }

  private checkIfShouldRedirectToDefaultSubState(): boolean {
    const state = this.$uiRouterGlobals.current.name;

    return state === BASE_OKR_VIEW_STATE;
  }

  private redirectToDefaultSubStateIfNeeded(): void {
    this.$state.go(`${BASE_OKR_VIEW_STATE}.all`, { okrViewId: this.$stateParams.okrViewId }, { location: "replace" });
  }

  private proceedLoadingOkrViewData(): void {
    this.isLoading = true;

    this.getOkrView().then(
      (okrView) => {
        this.okrView = okrView;
        this.isLoading = false;

        this.checkForNewlyCreatedView(okrView.id);
        this.okrViewEvents.broadcastOkrViewVisited(okrView);
      },
      (error) => {
        this.isLoading = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private getOkrView(): IPromise<ShareableFilter> {
    const shareableFilterDefer = this.$q.defer<ShareableFilter>();

    this.shareableFiltersFacade
      .getShareableFilterById$(this.$stateParams.okrViewId)
      .pipe(take(1))
      .subscribe({
        next: (shareableFilter) => {
          shareableFilterDefer.resolve(shareableFilter);
        },
        error: () => shareableFilterDefer.resolve({ id: "", name: "", filters: [], sorting: [] }),
      });

    return shareableFilterDefer.promise;
  }

  private checkForNewlyCreatedView = (currentViewId: string): void => {
    this.shareableFiltersModalsService.subscribeToViewCreationSuccess$().subscribe((newlyCreatedViewId) => {
      if (!newlyCreatedViewId || currentViewId !== newlyCreatedViewId) return;

      this.toastService.info("You just created a custom OKR view successfully", {
        uiPlacement: "bottom",
        uiOffset: 100,
        uiShouldTimeout: true,
        uiDuration: 3000,
        uiPrimaryCTA: "View all",
        uiOnPrimaryAction: () => {
          this.$state.go(BASE_OKR_VIEWS_STATE);
        },
      });
    });
  };

  private setMarkedAsFavorite(): void {
    this.isMarkedAsFavorite = this.favoritesManager.entityIsFavorite({ id: this.$stateParams.okrViewId });
    this.$scope.$evalAsync();
  }

  private getNavItem(): INavItem {
    if (!this.okrView) return;

    return this.favoritesManager.getFavoriteItem({ id: this.okrView.id }) || navItemsFactory.withRandomColor(navItemsFactory.buildFilterNavItem(this.okrView));
  }

  private getCurrentUserAllowedActions(): IPromise<IUserItemAllowedActions> {
    return this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$stateParams.okrViewId, "shareableFilter");
  }
}

export const OkrViewComponent: IComponentOptions = {
  controller: OkrViewCtrl,
  template: require("./okr-view.component.html"),
  bindings: {},
};
